<template>
  <div class="t-xmlcontainer">
    <div
      v-if="loaded"
      class="t-xmlcontent"
      v-html="xmlText"
      @click.capture.exact.left="handleClick"
    ></div>
    <div v-else class="t-xmlplaceholder">
      <div>
        Page {{ logicalPages[pageNum - 1] }}
        {{ loading ? '(loading)' : denied ? '(denied)' : '' }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.t-xmlplaceholder {
  display: flex;
  flex-direction: column;
  height: 30vh;
  justify-content: center;
}
</style>

<script>
import * as R from 'ramda'
import api from '@/services/api'
import MathJaxMixin from './MathJaxMixin.vue'

/**
 * Find the ancestor element that satisfies a function.
 */
const findAncestor = (start, fn, max = 100) => {
  for (let el = start, i = 0; el && i < max; i++) {
    if (fn(el)) {
      return el
    }
    el = el.parentElement
  }
}

/**
 * Given a pathname, return the dirname (all but the last segment).
 *   /x/y/z => /x/y
 *   /x/y/z/ => /x/y
 *   /x => /
 *   / => /
 */
const dirname = s =>
  ['', ...s.split('/').filter(Boolean).slice(0, -1)].join('/')

/**
 * Attempt to construct an URL, without throwing an exception.
 */
const tryUrl = R.tryCatch(s => new URL(s), R.always(undefined))

export default {
  name: 'XmlPage',

  mixins: [MathJaxMixin],

  props: {
    denied: {
      type: Object,
    },
    isCurrentPage: {
      type: Boolean,
      required: true,
    },
    shouldKeep: {
      type: Boolean,
      required: true,
    },
    shouldLoad: {
      type: Boolean,
      required: true,
    },
    pageNum: {
      type: Number,
      required: true,
    },
    xmlUrl: {
      type: String,
      default: null,
    },
  },

  // Don't populate unused props as div attributes.
  inheritAttrs: false,

  data() {
    return {
      loaded: false,
      loading: false,
      xmlText: null,
    }
  },

  watch: {
    loaded() {
      this.scrollAndHighlight()
    },

    $route() {
      this.scrollAndHighlight()
    },

    shouldKeep() {
      this.unloadPage()
    },

    shouldLoad() {
      this.loadPage()
    },

    xmlUrl() {
      this.loadPage()
    },
  },

  methods: {
    loadPage() {
      if (
        this.shouldLoad &&
        this.xmlUrl &&
        !this.denied &&
        !this.loading &&
        !this.loaded
      ) {
        this.loading = true
        api
          .getPageXml(this.xmlUrl)
          .then(xmlText => {
            this.xmlText = xmlText
            this.loading = false
            this.loaded = true
            this.$nextTick(() => {
              if (this.loaded) {
                this.hasMath = !!this.$el.getElementsByTagName('math').length
                if (this.hasMath) {
                  this.$mathjaxTypeset([this.$el])
                }
              }
            })
          })
          .catch(e => {
            // This might not be 403, instead it might be
            // a cross-origin redirect (EZproxy session timeout) or
            // another kind of error. But treating it as denied
            // allows Loader to handle it.
            this.setPage(this.pageNum, {
              denied: {e},
            })
          })
      }
    },

    unloadPage() {
      if (!this.shouldKeep) {
        if (this.hasMath) {
          this.$mathjaxClear([this.$el])
        }
        this.loaded = false
        this.xmlText = null
      }
    },

    handleClick(event) {
      const a = findAncestor(event.target, el => el.href !== undefined)
      if (!a) {
        return
      }

      const url = tryUrl(a.href)
      if (!url) {
        return
      }

      if (
        url.origin !== window.location.origin ||
        dirname(url.pathname) !== dirname(window.location.pathname)
      ) {
        return
      }

      let anchor = url.searchParams.get('anchor')
      if (!anchor) {
        return
      }

      event.preventDefault()

      this.goToAnchorPage(anchor, {hash: url.hash})
    },

    goToAnchorPage(anchor, options) {
      console.debug('looking up anchor', anchor)
      api.getAnchorPage(this.pubInfo.tizraId, anchor).then(pageNum => {
        if (!pageNum) {
          console.debug(`anchor ${anchor} doesn't appear in publication`)
        } else if (pageNum === this.pageNum) {
          console.debug('whoa, API says anchor is on current page')
        } else {
          console.debug('found anchor on page', pageNum)
          this.setPageNum(pageNum, {
            ...options,
            query: {...options?.query, anchor},
          })
        }
      })
    },

    scrollAndHighlight() {
      if (this.loaded) {
        // virtual DOM commit
        this.$nextTick(() => {
          if (this.isCurrentPage) {
            this.scrollToAnchor()
          }
          this.highlightHash()
        })
      }
    },

    scrollToAnchor() {
      const {
        hash,
        query: {anchor},
      } = this.$route

      // This race condition is (remotely) possible since this method is called
      // in nextTick.
      if (!this.$el) {
        console.log('whoa, XmlPage went away before scrollToAnchor')
        return
      }

      const anchorEl = anchor && this.$el.querySelector(`#${anchor}`)
      const hashEl = hash.length > 1 && this.$el.querySelector(hash)

      if (anchor && !anchorEl) {
        console.debug(
          "anchor doesn't appear on current page, attempting to navigate",
        )
        this.goToAnchorPage(anchor, {hash})
        return
      }

      // hash takes prio for scrolling
      const el = hashEl || anchorEl
      if (el) {
        el.scrollIntoView({alignToTop: true})
      }
    },

    highlightHash() {
      // This race condition is (remotely) possible since this method is called
      // in nextTick.
      if (!this.$el) {
        console.log('whoa, XmlPage went away before highlightHash')
        return
      }

      const {hash} = this.$route

      if (hash.length > 1) {
        const highlightEl = this.$el.querySelector(hash)
        // This is differentiated from t-highlight-search so that it's possible
        // to use different highlighting treatments. This will stay highlighted
        // until either the page unloads or #hash changes to something new (and
        // not empty).
        const highlightClass = 't-highlight-id'
        highlightEl?.classList.add(highlightClass)
        this.$el.querySelectorAll(`.${highlightClass}`).forEach(el => {
          if (el !== highlightEl) {
            el.classList.remove(highlightClass)
          }
        })
      }
    },
  },

  inject: ['logicalPages', 'pubInfo', 'setPage', 'setPageNum', 'settings'],
}
</script>
